/* ═══════════════════════════════════════════════════════════════════
   ZERO GRAVITY WATER — WebGL Ripple Shader
   Ported from 21st.dev/community water-ripple-image (React → vanilla JS).
   Renders gradient + floor text to an offscreen canvas, then distorts
   it with a real-time GLSL simplex-noise water shader.
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ──────── GLSL Shaders ──────── */

  const VERT = `
precision mediump float;
varying vec2 vUv;
attribute vec2 a_position;
void main() {
  vUv = .5 * (a_position + 1.);
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

  const FRAG = `
precision mediump float;

varying vec2 vUv;
uniform sampler2D u_image_texture;
uniform float u_time;
uniform float u_ratio;
uniform float u_img_ratio;
uniform float u_blueish;
uniform float u_scale;
uniform float u_illumination;
uniform float u_surface_distortion;
uniform float u_water_distortion;
uniform float u_speed;

#define TWO_PI 6.28318530718
#define PI 3.14159265358979323846

vec3 mod289(vec3 x) { return x - floor(x * (1. / 289.)) * 289.; }
vec2 mod289(vec2 x) { return x - floor(x * (1. / 289.)) * 289.; }
vec3 permute(vec3 x) { return mod289(((x*34.)+1.)*x); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1;
  i1 = (x0.x > x0.y) ? vec2(1., 0.) : vec2(0., 1.);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0., i1.y, 1.)) + i.x + vec3(0., i1.x, 1.));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.);
  m = m*m;
  m = m*m;
  vec3 x = 2. * fract(p * C.www) - 1.;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130. * dot(m, g);
}

mat2 rotate2D(float r) {
  return mat2(cos(r), sin(r), -sin(r), cos(r));
}

float surface_noise(vec2 uv, float t, float scale) {
  vec2 n = vec2(.1);
  vec2 N = vec2(.1);
  mat2 m = rotate2D(.5);
  for (int j = 0; j < 10; j++) {
    uv *= m;
    n *= m;
    vec2 q = uv * scale + float(j) + n + (.5 + .5 * float(j)) * (mod(float(j), 2.) - 1.) * t;
    n += sin(q);
    N += cos(q) / scale;
    scale *= 1.2;
  }
  return (N.x + N.y + .1);
}

void main() {
  vec2 uv = vUv;
  uv.y = 1. - uv.y;
  uv.x *= u_ratio;

  float t = u_speed * 0.002 * u_time;
  vec3 color = vec3(0.);
  float opacity = 0.;

  float outer_noise = snoise((.3 + .1 * sin(t)) * uv + vec2(0., .2 * t));
  vec2 surface_noise_uv = 2. * uv + (outer_noise * .2);

  float surf = surface_noise(surface_noise_uv, t, u_scale);
  surf *= pow(uv.y, .3);
  surf = pow(surf, 2.);

  vec2 img_uv = vUv;
  img_uv -= .5;
  if (u_ratio > u_img_ratio) {
    img_uv.x = img_uv.x * u_ratio / u_img_ratio;
  } else {
    img_uv.y = img_uv.y * u_img_ratio / u_ratio;
  }
  float scale_factor = 1.1;
  img_uv *= scale_factor;
  img_uv += .5;
  img_uv.y = 1. - img_uv.y;

  img_uv += (u_water_distortion * outer_noise);
  img_uv += (u_surface_distortion * surf);

  vec4 img = texture2D(u_image_texture, img_uv);
  img *= (1. + u_illumination * surf);

  color += img.rgb;
  color += u_illumination * vec3(1. - u_blueish, 1., 1.) * surf;
  opacity += img.a;


  gl_FragColor = vec4(color, opacity);
}
`;

  /* ──────── GL helpers ──────── */

  function compileShader(gl, src, type) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.error('[zgp-water] Shader error:', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function createProgram(gl, vs, fs) {
    var v = compileShader(gl, vs, gl.VERTEX_SHADER);
    var f = compileShader(gl, fs, gl.FRAGMENT_SHADER);
    if (!v || !f) return null;
    var prog = gl.createProgram();
    gl.attachShader(prog, v);
    gl.attachShader(prog, f);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error('[zgp-water] Program link error:', gl.getProgramInfoLog(prog));
      gl.deleteProgram(prog);
      return null;
    }
    return prog;
  }

  /* ──────── Offscreen texture canvas ──────── */

  function buildTextureCanvas(section, bgImage) {
    var data = section.dataset;
    var w = Math.max(section.offsetWidth, 800);
    var h = Math.max(section.offsetHeight, 600);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    var off = document.createElement('canvas');
    off.width = w * dpr;
    off.height = h * dpr;
    var ctx = off.getContext('2d');
    ctx.scale(dpr, dpr);

    /* Water gradient background */
    var waterTop = data.zgpWaterTop || '#6FD8E0';
    var waterBottom = data.zgpWaterBottom || '#0E6B8A';
    var grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, lightenColor(waterTop, 0.15));
    grad.addColorStop(0.35, waterTop);
    grad.addColorStop(1, waterBottom);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    /* Subtle tile grid lines */
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    var gridSize = 80;
    for (var gx = 0; gx <= w; gx += gridSize) {
      ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke();
    }
    for (var gy = 0; gy <= h; gy += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();
    }

    var bgMotion = data.zgpBgMotion || 'waves';
    if (bgMotion === 'stationary') {
      return off;
    }

    var bgType = data.zgpBgType || 'image';

    if (bgType === 'image' && bgImage) {
      var imgW = bgImage.naturalWidth || bgImage.width;
      var imgH = bgImage.naturalHeight || bgImage.height;
      if (imgW > 0 && imgH > 0) {
        var scale = Math.min(w * 0.4 / imgW, h * 0.4 / imgH, 1);
        var drawW = imgW * scale;
        var drawH = imgH * scale;
        ctx.globalAlpha = 0.92;
        ctx.drawImage(bgImage, (w - drawW) / 2, (h - drawH) / 2, drawW, drawH);
      }
    } else if (bgType === 'text') {
      /* Floor text */
    var text = data.zgpFloorText || 'AFEWPIECES';
    var textColor = data.zgpTextColor || '#0A0A0A';
    var textSize = parseInt(data.zgpTextSize, 10) || 200;
    var textSpacing = parseInt(data.zgpTextSpacing, 10) || -4;
    var textFont = data.zgpTextFont || 'display';

    // Scale text size proportionally to canvas width
    var scaledSize = textSize * (w / 1440);
    scaledSize = Math.max(40, Math.min(scaledSize, w * 0.25));

    var fontFamily = textFont === 'mono'
      ? "'Space Mono', 'Courier New', Courier, monospace"
      : "'Helvetica Neue', 'Arial Black', Helvetica, Arial, sans-serif";

    ctx.save();
    ctx.font = '900 ' + scaledSize + 'px ' + fontFamily;
    ctx.fillStyle = textColor;
    ctx.globalAlpha = 0.92;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Apply letter-spacing by drawing each character individually
    if (textSpacing !== 0) {
      var chars = text.split('');
      var totalWidth = 0;
      chars.forEach(function (ch) {
        totalWidth += ctx.measureText(ch).width + textSpacing;
      });
      totalWidth -= textSpacing; // Remove trailing spacing

      var startX = (w - totalWidth) / 2;
      var textY = h / 2;
      chars.forEach(function (ch) {
        var chW = ctx.measureText(ch).width;
        ctx.fillText(ch, startX + chW / 2, textY);
        startX += chW + textSpacing;
      });
    } else {
      ctx.fillText(text, w / 2, h / 2);
    }
    ctx.restore();
    }

    return off;
  }

  function lightenColor(hex, amount) {
    hex = hex.replace('#', '');
    var r = parseInt(hex.substring(0, 2), 16);
    var g = parseInt(hex.substring(2, 4), 16);
    var b = parseInt(hex.substring(4, 6), 16);
    r = Math.min(255, Math.round(r + (255 - r) * amount));
    g = Math.min(255, Math.round(g + (255 - g) * amount));
    b = Math.min(255, Math.round(b + (255 - b) * amount));
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  /* ──────── Main class ──────── */

  function ZeroGravityWater(section) {
    this.section = section;
    this.canvas = section.querySelector('[data-zgp-water]');
    if (!this.canvas) return;

    this.gl = null;
    this.program = null;
    this.uniforms = {};
    this.texture = null;
    this.animId = null;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.bgImage = null;

    // Shader parameters from data attributes
    this.params = {
      blueish: parseFloat(section.dataset.zgpBlueish) || 0.5,
      scale: parseFloat(section.dataset.zgpScale) || 7,
      illumination: parseFloat(section.dataset.zgpIllumination) || 0.15,
      surfaceDistortion: parseFloat(section.dataset.zgpSurfaceDistortion) || 0.05,
      waterDistortion: parseFloat(section.dataset.zgpWaterDistortion) || 0.03,
      speed: parseFloat(section.dataset.zgpSpeed) || 1.0,
    };

    if (!this._initGL()) return;
    
    var bgType = section.dataset.zgpBgType || 'image';
    var bgMotion = section.dataset.zgpBgMotion || 'waves';
    var bgImageUrl = section.dataset.zgpBgImageUrl;

    if (bgType === 'image' && bgMotion === 'waves' && bgImageUrl) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      var self = this;
      img.onload = function() {
        self.bgImage = img;
        self._buildTexture();
      };
      img.src = bgImageUrl;
    }

    this._buildTexture();
    this._resize();
    this._bindResize();
    this._bindVisibility();
    this._start();
  }

  ZeroGravityWater.prototype._initGL = function () {
    var gl = this.canvas.getContext('webgl', { alpha: true, antialias: true })
      || this.canvas.getContext('experimental-webgl', { alpha: true, antialias: true });

    if (!gl) {
      console.warn('[zgp-water] WebGL not supported — falling back to CSS gradient');
      this.canvas.style.display = 'none';
      return false;
    }
    this.gl = gl;

    var program = createProgram(gl, VERT, FRAG);
    if (!program) return false;
    this.program = program;
    gl.useProgram(program);

    // Collect all uniform locations
    var count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < count; i++) {
      var info = gl.getActiveUniform(program, i);
      if (info) this.uniforms[info.name] = gl.getUniformLocation(program, info.name);
    }

    // Fullscreen quad
    var vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    var vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    var posLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    // Push initial uniforms
    this._updateUniforms();

    return true;
  };

  ZeroGravityWater.prototype._updateUniforms = function () {
    var gl = this.gl;
    var u = this.uniforms;
    gl.uniform1f(u['u_blueish'], this.params.blueish);
    gl.uniform1f(u['u_scale'], this.params.scale);
    gl.uniform1f(u['u_illumination'], this.params.illumination);
    gl.uniform1f(u['u_surface_distortion'], this.params.surfaceDistortion);
    gl.uniform1f(u['u_water_distortion'], this.params.waterDistortion);
    gl.uniform1f(u['u_speed'], this.params.speed);
  };

  ZeroGravityWater.prototype._buildTexture = function () {
    var gl = this.gl;
    var offCanvas = buildTextureCanvas(this.section, this.bgImage);

    if (this.texture) gl.deleteTexture(this.texture);
    var tex = gl.createTexture();
    this.texture = tex;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, offCanvas);

    gl.uniform1i(this.uniforms['u_image_texture'], 0);

    var imgRatio = offCanvas.width / offCanvas.height;
    gl.uniform1f(this.uniforms['u_img_ratio'], imgRatio);
  };

  ZeroGravityWater.prototype._resize = function () {
    var gl = this.gl;
    var canvas = this.canvas;
    var w = Math.floor(this.section.offsetWidth * this.dpr);
    var h = Math.floor(this.section.offsetHeight * this.dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    canvas.style.width = this.section.offsetWidth + 'px';
    canvas.style.height = this.section.offsetHeight + 'px';
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform1f(this.uniforms['u_ratio'], canvas.width / canvas.height);
  };

  ZeroGravityWater.prototype._bindResize = function () {
    var self = this;
    var timeout;
    window.addEventListener('resize', function () {
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        self._resize();
        self._buildTexture(); // rebuild texture at new dimensions
      }, 150);
    });
  };

  ZeroGravityWater.prototype._bindVisibility = function () {
    var self = this;
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self._stop();
      else self._start();
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) self._start();
          else self._stop();
        });
      }, { threshold: 0.05 });
      io.observe(this.section);
    }
  };

  ZeroGravityWater.prototype._start = function () {
    if (this.animId) return;
    var self = this;
    function render() {
      var gl = self.gl;
      if (self.uniforms['u_time']) {
        gl.uniform1f(self.uniforms['u_time'], performance.now());
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      self.animId = requestAnimationFrame(render);
    }
    this.animId = requestAnimationFrame(render);
  };

  ZeroGravityWater.prototype._stop = function () {
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  };

  /* ──────── Init ──────── */

  function init() {
    var sections = document.querySelectorAll('[data-zgp]');
    sections.forEach(function (section) {
      if (section.__zgpWaterBooted) return;
      section.__zgpWaterBooted = true;
      new ZeroGravityWater(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
